import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  XtWsDepthData,
  XtWsDepthUpdateData,
  XtWsKlineData,
  XtWsPushMessage,
  XtWsSubscribeRequest,
  XtWsSubscribeResponse,
  XtWsTickerData,
  XtWsTradeData,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const XT_WS_BASE_URL = 'wss://stream.xt.com/public';

/**
 * XT WebSocket channel descriptors.
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public
 */
export const XT_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'depth@<symbol>,<levels>',
    supportsBatching: true,
    docUrl: 'https://doc.xt.com/docs/spot/WebSocket%20Public/LimitedDepth',
  },
  depthUpdate: {
    channel: 'depthUpdate',
    topic: 'depth_update@<symbol>',
    supportsBatching: true,
    docUrl: 'https://doc.xt.com/docs/spot/WebSocket%20Public/IncrementalDepth',
  },
  ticker: {
    channel: 'ticker',
    topic: 'ticker@<symbol>',
    supportsBatching: true,
    docUrl: 'https://doc.xt.com/docs/spot/WebSocket%20Public/Ticker',
  },
  klines: {
    channel: 'klines',
    topic: 'kline@<symbol>,<interval>',
    supportsBatching: true,
    docUrl: 'https://doc.xt.com/docs/spot/WebSocket%20Public/Kline',
  },
  trades: {
    channel: 'trades',
    topic: 'trade@<symbol>',
    supportsBatching: true,
    docUrl: 'https://doc.xt.com/docs/spot/WebSocket%20Public/TradeRecord',
  },
};

/**
 * XT WebSocket topology information.
 * Messages are plain JSON (no compression).
 * Client must send "ping" text periodically; server replies with "pong" text.
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/Heartbeat
 */
export const XT_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: XT_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 40000, // Recommended: 40 seconds (timeout: 1 minute)
  note: 'Plain JSON messages (no compression). Client sends "ping" text, server replies "pong". Supports batched subscriptions. Orderbook uses incremental updates with sequence tracking.',
};

// ========================================
// Helper Types and Constants
// ========================================

/**
 * XT supported kline intervals for WebSocket.
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/Kline
 */
export type XtWsKlineInterval =
  | '1m'
  | '3m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '6h'
  | '8h'
  | '12h'
  | '1d'
  | '3d'
  | '1w'
  | '1M';

/**
 * XT depth levels for limited depth channel.
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/LimitedDepth
 */
export type XtDepthLevel = 5 | 10 | 20 | 50;

export type XtWsStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';

// ========================================
// WebSocket Message Handling
// ========================================

/**
 * Check if message is ping/pong text (not JSON).
 */
export function isXtPingPong(data: string): boolean {
  return data === 'ping' || data === 'pong';
}

/**
 * Type guard for XtWsSubscribeResponse.
 * Validates both property existence and correct types.
 * @see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 */
export function isXtWsSubscribeResponse(value: unknown): value is XtWsSubscribeResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<XtWsSubscribeResponse>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.code === 'number' &&
    typeof candidate.msg === 'string'
  );
}

/**
 * Create ping message for XT WebSocket.
 */
export function createXtPing(): string {
  return 'ping';
}

/**
 * Create subscribe request for XT WebSocket.
 * @param channels - Array of channel strings (e.g., ["ticker@btc_usdt", "kline@btc_usdt,5m"])
 */
export function createXtSubscribeRequest(channels: string[]): XtWsSubscribeRequest {
  return {
    method: 'subscribe',
    params: channels,
    id: `sub_${Date.now()}`,
  };
}

/**
 * Create unsubscribe request for XT WebSocket.
 */
export function createXtUnsubscribeRequest(channels: string[]): XtWsSubscribeRequest {
  return {
    method: 'unsubscribe',
    params: channels,
    id: `unsub_${Date.now()}`,
  };
}

/**
 * Build ticker channel string.
 * @example buildXtTickerChannel("btc_usdt") // "ticker@btc_usdt"
 */
export function buildXtTickerChannel(symbol: string): string {
  return `ticker@${symbol}`;
}

/**
 * Build kline channel string.
 * @example buildXtKlineChannel("btc_usdt", "5m") // "kline@btc_usdt,5m"
 */
export function buildXtKlineChannel(symbol: string, interval: XtWsKlineInterval): string {
  return `kline@${symbol},${interval}`;
}

/**
 * Build limited depth channel string.
 * @example buildXtDepthChannel("btc_usdt", 20) // "depth@btc_usdt,20"
 */
export function buildXtDepthChannel(symbol: string, levels: XtDepthLevel = 20): string {
  return `depth@${symbol},${levels}`;
}

/**
 * Build incremental depth channel string.
 * @example buildXtDepthUpdateChannel("btc_usdt") // "depth_update@btc_usdt"
 */
export function buildXtDepthUpdateChannel(symbol: string): string {
  return `depth_update@${symbol}`;
}

/**
 * Build trade channel string.
 * @example buildXtTradeChannel("btc_usdt") // "trade@btc_usdt"
 */
export function buildXtTradeChannel(symbol: string): string {
  return `trade@${symbol}`;
}

// ========================================
// WebSocket Subscription Options
// ========================================

export interface XtWsSubscriptionOptions<T> {
  /** Channel params (e.g., ["ticker@btc_usdt"]) */
  channels: string[];
  /** Callback for successfully decoded messages */
  onMessage: (data: XtWsPushMessage<T>) => void;
  /** Callback for errors (connection, decode, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
  /** Base WebSocket URL (defaults to XT_WS_BASE_URL) */
  baseUrl?: string;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

// ========================================
// Generic WebSocket Subscription
// ========================================

/**
 * Create a generic XT WebSocket subscription.
 * Handles ping/pong heartbeat and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 */
function createXtWsSubscription<T>(options: XtWsSubscriptionOptions<T>): ReconnectingWebSocket {
  const {
    channels,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = XT_WS_BASE_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Send subscription request
    const subscribeMsg = createXtSubscribeRequest(channels);
    ws.send(JSON.stringify(subscribeMsg));

    // Start heartbeat (ping every 40 seconds)
    if (pingInterval) {
      clearInterval(pingInterval);
    }
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(createXtPing());
      }
    }, XT_WS_TOPOLOGY.heartbeatIntervalMs);
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      const data = event.data as string;

      // Handle ping/pong text messages (not JSON)
      if (isXtPingPong(data)) {
        // Server sent pong response, no action needed
        // Or server sent ping, we respond with pong (though XT uses client-initiated ping)
        return;
      }

      // Parse JSON message
      const payload = JSON.parse(data) as XtWsSubscribeResponse | XtWsPushMessage<T>;

      // Handle subscription response
      if ('code' in payload && 'id' in payload) {
        const subResponse = payload as XtWsSubscribeResponse;
        if (subResponse.code !== 0) {
          onError?.(
            new Error(`XT subscription error: code=${subResponse.code}, msg=${subResponse.msg}`),
          );
        }
        return;
      }

      // Handle push message
      if ('topic' in payload && 'event' in payload && 'data' in payload) {
        onMessage(payload as XtWsPushMessage<T>);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`XT WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  // Cleanup on close
  const originalClose = ws.close.bind(ws);
  ws.close = (code?: number, reason?: string) => {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    originalClose(code, reason);
  };

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface XtTickerSubscriptionOptions {
  /** Lowercase symbol with underscore (e.g., "btc_usdt") */
  symbol: string;
  /** Callback for ticker updates */
  onMessage: (ticker: XtWsPushMessage<XtWsTickerData>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to XT ticker updates via WebSocket.
 * Rate: 1000ms (1 second).
 *
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/Ticker
 */
export function subscribeXtTicker(options: XtTickerSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<XtWsTickerData>({
    channels: [buildXtTickerChannel(symbol)],
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Kline (Candlestick) Subscription
// ========================================

export interface XtKlineSubscriptionOptions {
  /** Lowercase symbol with underscore (e.g., "btc_usdt") */
  symbol: string;
  /** Kline interval */
  interval: XtWsKlineInterval;
  /** Callback for kline updates */
  onMessage: (kline: XtWsPushMessage<XtWsKlineData>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to XT kline (candlestick) updates via WebSocket.
 * Rate: 1000ms (1 second).
 *
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/Kline
 */
export function subscribeXtKlines(options: XtKlineSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<XtWsKlineData>({
    channels: [buildXtKlineChannel(symbol, interval)],
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Limited Depth Subscription
// ========================================

export interface XtDepthSubscriptionOptions {
  /** Lowercase symbol with underscore (e.g., "btc_usdt") */
  symbol: string;
  /** Depth levels: 5, 10, 20, or 50 (default: 20) */
  levels?: XtDepthLevel;
  /** Callback for depth updates */
  onMessage: (depth: XtWsPushMessage<XtWsDepthData>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to XT limited depth (snapshot) updates via WebSocket.
 * Rate: 1000ms (1 second).
 *
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/LimitedDepth
 */
export function subscribeXtDepth(options: XtDepthSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, levels = 20, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<XtWsDepthData>({
    channels: [buildXtDepthChannel(symbol, levels)],
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Incremental Depth Subscription
// ========================================

export interface XtDepthUpdateSubscriptionOptions {
  /** Lowercase symbol with underscore (e.g., "btc_usdt") */
  symbol: string;
  /** Callback for incremental depth updates */
  onMessage: (depthUpdate: XtWsPushMessage<XtWsDepthUpdateData>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to XT incremental depth updates via WebSocket.
 * Rate: 100ms. Use with REST snapshot for orderbook management.
 *
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/IncrementalDepth
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/OrderbookManage
 */
export function subscribeXtDepthUpdate(
  options: XtDepthUpdateSubscriptionOptions,
): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<XtWsDepthUpdateData>({
    channels: [buildXtDepthUpdateChannel(symbol)],
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Trade Subscription
// ========================================

export interface XtTradeSubscriptionOptions {
  /** Lowercase symbol with underscore (e.g., "btc_usdt") */
  symbol: string;
  /** Callback for trade updates */
  onMessage: (trade: XtWsPushMessage<XtWsTradeData>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to XT public trade updates via WebSocket.
 * Rate: Real-time (as trades occur).
 *
 * @see https://doc.xt.com/docs/spot/WebSocket%20Public/TradeRecord
 */
export function subscribeXtTrades(options: XtTradeSubscriptionOptions): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<XtWsTradeData>({
    channels: [buildXtTradeChannel(symbol)],
    onMessage,
    onError,
    onStatusChange,
  });
}

// ========================================
// Multi-Channel Subscription
// ========================================

export interface XtMultiChannelSubscriptionOptions {
  /** Array of channel strings (e.g., ["ticker@btc_usdt", "kline@btc_usdt,5m"]) */
  channels: string[];
  /** Callback for all messages (caller must handle topic routing) */
  onMessage: (message: XtWsPushMessage<unknown>) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: XtWsStatus) => void;
}

/**
 * Subscribe to multiple XT channels via a single WebSocket connection.
 * Useful for subscribing to ticker, klines, depth, and trades together.
 *
 * @example
 * const ws = subscribeXtMultiChannel({
 *   channels: [
 *     buildXtTickerChannel("btc_usdt"),
 *     buildXtKlineChannel("btc_usdt", "5m"),
 *     buildXtDepthUpdateChannel("btc_usdt"),
 *     buildXtTradeChannel("btc_usdt"),
 *   ],
 *   onMessage: (msg) => {
 *     switch (msg.topic) {
 *       case "ticker": // handle ticker
 *       case "kline": // handle kline
 *       case "depth_update": // handle depth
 *       case "trade": // handle trade
 *     }
 *   }
 * });
 */
export function subscribeXtMultiChannel(
  options: XtMultiChannelSubscriptionOptions,
): ReconnectingWebSocket {
  const { channels, onMessage, onError, onStatusChange } = options;

  return createXtWsSubscription<unknown>({
    channels,
    onMessage,
    onError,
    onStatusChange,
  });
}
